import { z } from 'zod';
import { paginationQuery } from './common.js';
/* ------------------------------------------------------------------ */
/* Módulo Proyectos (fase 2): personal, horas y planilla              */
/* ------------------------------------------------------------------ */
/**
 * Aviso obligatorio en toda pantalla de planilla: el módulo calcula costo de mano de obra y saldos por pagar con
 * la legislación de jornada (Código de Trabajo), pero no es una planilla oficial.
 */
export const PAYROLL_DISCLAIMER = 'Esta herramienta calcula costo de mano de obra y saldos por pagar; no es una planilla oficial (no incluye aguinaldo, vacaciones, CCSS obrera ni renta).';
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
const money = z.coerce.number().min(0, 'No puede ser negativo').max(1e12, 'Monto inválido');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Hora inválida (hh:mm)');
const isoDate = z.iso.date('Fecha inválida');
/** Días de la semana con 0 = domingo (la convención de `rest_day`). */
export const WEEK_DAYS = [
    { code: 0, name: 'Domingo', short: 'Dom' },
    { code: 1, name: 'Lunes', short: 'Lun' },
    { code: 2, name: 'Martes', short: 'Mar' },
    { code: 3, name: 'Miércoles', short: 'Mié' },
    { code: 4, name: 'Jueves', short: 'Jue' },
    { code: 5, name: 'Viernes', short: 'Vie' },
    { code: 6, name: 'Sábado', short: 'Sáb' },
];
/** Días laborables del horario en ISODOW (1 = lunes … 7 = domingo). */
export const ISO_WEEK_DAYS = [
    { code: 1, name: 'Lunes', short: 'L' },
    { code: 2, name: 'Martes', short: 'K' },
    { code: 3, name: 'Miércoles', short: 'M' },
    { code: 4, name: 'Jueves', short: 'J' },
    { code: 5, name: 'Viernes', short: 'V' },
    { code: 6, name: 'Sábado', short: 'S' },
    { code: 7, name: 'Domingo', short: 'D' },
];
/* ---------------------------- puestos ---------------------------- */
/** Categorías del Decreto de Salarios Mínimos (referencia; el sistema no valida contra los mínimos vigentes). */
export const MTSS_CATEGORIES = [
    { code: 'no_calificado', name: 'Trabajador no calificado' },
    { code: 'semicalificado', name: 'Trabajador semicalificado' },
    { code: 'calificado', name: 'Trabajador calificado' },
    { code: 'especializado', name: 'Trabajador especializado' },
    { code: 'tecnico', name: 'Técnico' },
    { code: 'profesional', name: 'Profesional' },
];
export const mtssCategory = z.enum([
    'no_calificado',
    'semicalificado',
    'calificado',
    'especializado',
    'tecnico',
    'profesional',
]);
export const workerPositionInput = z
    .object({
    name: z.string().trim().min(2, 'Requerido').max(120),
    dailyRate: money.refine((n) => n > 0, 'Ingresá el jornal'),
    /** Tarifa por hora ordinaria diurna sugerida (vacía = jornal ÷ 8 al asignar). */
    hourlyRate: money.nullable().optional(),
    mtssCategory: mtssCategory.nullable().optional(),
    /** Art. 143: puesto de confianza sin limitación de jornada (hasta 12 h diarias sin hora extra). */
    excludedFromLimits: z.boolean().default(false),
    isActive: z.boolean().default(true),
})
    .superRefine((v, ctx) => {
    if (v.hourlyRate != null && v.hourlyRate > 0 && v.hourlyRate >= v.dailyRate) {
        ctx.addIssue({
            code: 'custom',
            path: ['hourlyRate'],
            message: 'La tarifa por hora debe ser menor que el jornal',
        });
    }
});
/* ---------------------------- trabajadores ---------------------------- */
export const WORKER_ID_TYPES = [
    { code: '01', name: 'Cédula física' },
    { code: '03', name: 'DIMEX' },
    { code: 'PP', name: 'Pasaporte' },
    { code: 'OT', name: 'Otro' },
];
export const workerIdType = z.enum(['01', '03', 'PP', 'OT']);
export const workerInput = z
    .object({
    name: z.string().trim().min(3, 'Escribí el nombre completo').max(160),
    identificationType: workerIdType.default('01'),
    identification: z
        .string()
        .trim()
        .max(30)
        .transform((v) => v.replace(/[\s-]/g, ''))
        .nullable()
        .optional(),
    phone: optionalText(30),
    email: z
        .email('Correo inválido')
        .trim()
        .toLowerCase()
        .max(160)
        .nullable()
        .optional()
        .or(z.literal('').transform(() => null)),
    defaultPositionId: z.uuid().nullable().optional(),
    bankName: optionalText(80),
    iban: z
        .string()
        .trim()
        .max(40)
        .transform((v) => v.replace(/\s/g, '').toUpperCase())
        .nullable()
        .optional(),
    notes: optionalText(2000),
    /** En modo «por trabajador», solo quienes lo tienen cotizan CCSS e INS. */
    subjectToCharges: z.boolean().default(false),
    isActive: z.boolean().default(true),
})
    .superRefine((v, ctx) => {
    const id = v.identification ?? '';
    if (id && v.identificationType === '01' && !/^[1-9]\d{8}$/.test(id)) {
        ctx.addIssue({ code: 'custom', path: ['identification'], message: 'La cédula física tiene 9 dígitos' });
    }
    if (id && v.identificationType === '03' && !/^\d{11,12}$/.test(id)) {
        ctx.addIssue({ code: 'custom', path: ['identification'], message: 'El DIMEX tiene 11 o 12 dígitos' });
    }
    if (v.iban && !/^CR\d{20}$/.test(v.iban)) {
        ctx.addIssue({
            code: 'custom',
            path: ['iban'],
            message: 'El IBAN de Costa Rica es CR seguido de 20 dígitos',
        });
    }
});
export const workerListQuery = paginationQuery.extend({
    status: z.enum(['active', 'inactive', 'all']).optional(),
    positionId: z.uuid().optional(),
    /** Solo los que tienen saldo por pagar. */
    withBalance: z.coerce.boolean().optional(),
});
/* ---------------------------- asignaciones ---------------------------- */
export const PAY_MODALITIES = [
    { code: 'daily', name: 'Por día (jornal)' },
    { code: 'hourly', name: 'Por hora' },
    { code: 'monthly', name: 'Mensual (pago quincenal)' },
];
export const payModality = z.enum(['daily', 'hourly', 'monthly']);
export const assignmentInput = z
    .object({
    workerId: z.uuid('Elegí el trabajador'),
    positionId: z.uuid('Elegí el puesto'),
    modality: payModality.default('daily'),
    /** Jornal (por día) o tarifa de la hora ordinaria diurna (por hora). En mensual lo calcula el servidor. */
    rate: z.coerce.number().gt(0, 'Ingresá la tarifa').max(1e9).optional(),
    /** Salario mensual. El jornal guardado es ese monto ÷ 30. */
    monthlySalary: z.coerce.number().gt(0, 'Ingresá el salario mensual').max(1e9).nullable().optional(),
    /** Descanso semanal (Art. 152): 0 = domingo … 6 = sábado. */
    restDay: z.coerce.number().int().min(0).max(6).default(0),
    startDate: isoDate.nullable().optional(),
    endDate: isoDate.nullable().optional(),
    isActive: z.boolean().default(true),
    notes: optionalText(500),
})
    .superRefine((v, ctx) => {
    if (v.modality === 'monthly') {
        if (!(Number(v.monthlySalary) > 0)) {
            ctx.addIssue({ code: 'custom', path: ['monthlySalary'], message: 'Ingresá el salario mensual' });
        }
    }
    else if (!(Number(v.rate) > 0)) {
        ctx.addIssue({ code: 'custom', path: ['rate'], message: 'Ingresá la tarifa' });
    }
    if (v.startDate && v.endDate && v.endDate < v.startDate) {
        ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Debe ser posterior al inicio' });
    }
});
export const assignmentUpdateInput = z
    .object({
    positionId: z.uuid().optional(),
    modality: payModality.optional(),
    rate: z.coerce.number().gt(0, 'Ingresá la tarifa').max(1e9).optional(),
    monthlySalary: z.coerce.number().gt(0, 'Ingresá el salario mensual').max(1e9).nullable().optional(),
    restDay: z.coerce.number().int().min(0).max(6).optional(),
    startDate: isoDate.nullable().optional(),
    endDate: isoDate.nullable().optional(),
    isActive: z.boolean().optional(),
    notes: optionalText(500),
})
    .superRefine((v, ctx) => {
    if (v.startDate && v.endDate && v.endDate < v.startDate) {
        ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'Debe ser posterior al inicio' });
    }
});
/* ---------------------------- horario del proyecto ---------------------------- */
export const dayOverrideInput = z.object({
    workStart: hhmm,
    workEnd: hhmm,
    lunchMinutes: z.coerce.number().int().min(0).max(240).default(0),
});
/** Horario, recargos y viáticos del proyecto: alimentan el cálculo de cada día trabajado. */
export const projectScheduleInput = z.object({
    workStart: hhmm.default('07:00'),
    workEnd: hhmm.default('16:00'),
    lunchMinutes: z.coerce
        .number()
        .int()
        .min(0, 'No puede ser negativo')
        .max(240, 'Máximo 240 minutos')
        .default(60),
    lunchPaid: z.boolean().default(false),
    /** ISODOW 1 = lunes … 7 = domingo. */
    workDays: z
        .array(z.coerce.number().int().min(1).max(7))
        .min(1, 'Elegí al menos un día')
        .max(7)
        .transform((days) => [...new Set(days)].sort((a, b) => a - b))
        .default([1, 2, 3, 4, 5, 6]),
    /** Horario distinto por día (ISODOW como clave), p. ej. sábado de 7 a 12. */
    dayOverrides: z
        .record(z.string().regex(/^[1-7]$/), dayOverrideInput)
        .default({})
        .refine((r) => Object.keys(r).length <= 7, 'Demasiadas excepciones'),
    overtimeMultiplier: z.coerce
        .number()
        .min(1.5, 'La hora extra se paga al menos a tiempo y medio (Art. 139)')
        .max(5)
        .default(1.5),
    holidayMultiplier: z.coerce
        .number()
        .min(2, 'El feriado obligatorio se paga al menos doble (Art. 148)')
        .max(5)
        .default(2),
    /** Permite registrar días de más de 12 h con advertencia (Art. 140: solo siniestro o riesgo inminente). */
    allowOver12h: z.boolean().default(false),
    perDiemAmount: money.default(0),
    transportAmount: money.default(0),
});
/* ---------------------------- entradas de tiempo ---------------------------- */
export const ENTRY_KINDS = [
    { code: 'work', name: 'Trabajo' },
    { code: 'overtime', name: 'Hora extra' },
    { code: 'holiday', name: 'Feriado no laborado' },
    { code: 'rest_day', name: 'Descanso' },
    { code: 'advance', name: 'Adelanto' },
    { code: 'per_diem', name: 'Dieta' },
    { code: 'transport', name: 'Transporte' },
    { code: 'settlement', name: 'Liquidación' },
    { code: 'salary', name: 'Salario de quincena' },
    { code: 'absence', name: 'Ausencia' },
];
export const ABSENCE_REASONS = [
    { code: 'injustificada', name: 'Injustificada', deducts: true },
    { code: 'permiso_sin_goce', name: 'Permiso sin goce', deducts: true },
    { code: 'permiso_con_goce', name: 'Permiso con goce', deducts: false },
    { code: 'vacaciones', name: 'Vacaciones', deducts: false },
    { code: 'incapacidad', name: 'Incapacidad', deducts: false },
];
export const absenceReason = z.enum([
    'injustificada',
    'permiso_sin_goce',
    'permiso_con_goce',
    'vacaciones',
    'incapacidad',
]);
export const entryKind = z.enum([
    'work',
    'overtime',
    'holiday',
    'rest_day',
    'advance',
    'per_diem',
    'transport',
    'settlement',
    'salary',
    'absence',
]);
/**
 * Lo que se puede registrar a mano. El descanso trabajado se paga ×2 solo al registrar trabajo en el día de
 * descanso de la asignación (una sola definición, Art. 152); las liquidaciones llegan con el cierre de periodo.
 */
export const creatableEntryKind = z.enum([
    'work',
    'overtime',
    'holiday',
    'advance',
    'per_diem',
    'transport',
    'absence',
]);
export const JORNADA_TYPES = [
    { code: 'diurna', name: 'Diurna' },
    { code: 'mixta', name: 'Mixta' },
    { code: 'nocturna', name: 'Nocturna' },
];
export const timeEntryInput = z
    .object({
    kind: creatableEntryKind,
    date: isoDate,
    timeIn: hhmm.nullable().optional(),
    timeOut: hhmm.nullable().optional(),
    lunchMinutes: z.coerce.number().int().min(0).max(240, 'Máximo 240 minutos').nullable().optional(),
    /** Horas (hora extra) o cantidad (dieta, transporte). */
    quantity: z.coerce.number().min(0).max(1000).nullable().optional(),
    /** Monto del adelanto, o tarifa propuesta de la hora extra (el servidor aplica el mínimo legal). */
    amount: z.coerce.number().min(0).max(1e10).nullable().optional(),
    notes: optionalText(500),
    /** Solo en ausencia de una asignación mensual. */
    absenceReason: absenceReason.nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.kind === 'work') {
        if (!v.timeIn)
            ctx.addIssue({ code: 'custom', path: ['timeIn'], message: 'Indicá la hora de entrada' });
        if (!v.timeOut)
            ctx.addIssue({ code: 'custom', path: ['timeOut'], message: 'Indicá la hora de salida' });
        if (v.timeIn && v.timeOut && v.timeIn === v.timeOut) {
            ctx.addIssue({
                code: 'custom',
                path: ['timeOut'],
                message: 'La salida debe ser distinta de la entrada',
            });
        }
    }
    if (v.kind === 'overtime' && !(Number(v.quantity) > 0)) {
        ctx.addIssue({ code: 'custom', path: ['quantity'], message: 'Indicá las horas extra' });
    }
    if (v.kind === 'advance' && !(Number(v.amount) > 0)) {
        ctx.addIssue({ code: 'custom', path: ['amount'], message: 'Indicá el monto del adelanto' });
    }
    if (v.kind === 'absence') {
        if (!v.absenceReason) {
            ctx.addIssue({ code: 'custom', path: ['absenceReason'], message: 'Elegí el motivo' });
        }
        const qty = Number(v.quantity);
        if (!(qty >= 0.5) || qty > 31 || Math.round(qty * 2) !== qty * 2) {
            ctx.addIssue({
                code: 'custom',
                path: ['quantity'],
                message: 'Los días van de 0,5 a 31, de medio día en medio día',
            });
        }
    }
});
/** Edición: el tipo no cambia (para cambiarlo, borrá y registrá de nuevo). */
export const timeEntryUpdateInput = z
    .object({
    date: isoDate.optional(),
    timeIn: hhmm.nullable().optional(),
    timeOut: hhmm.nullable().optional(),
    lunchMinutes: z.coerce.number().int().min(0).max(240, 'Máximo 240 minutos').nullable().optional(),
    quantity: z.coerce.number().min(0).max(1000).nullable().optional(),
    amount: z.coerce.number().min(0).max(1e10).nullable().optional(),
    notes: optionalText(500),
    absenceReason: absenceReason.nullable().optional(),
})
    .superRefine((v, ctx) => {
    if (v.timeIn && v.timeOut && v.timeIn === v.timeOut) {
        ctx.addIssue({
            code: 'custom',
            path: ['timeOut'],
            message: 'La salida debe ser distinta de la entrada',
        });
    }
});
/** Registro grupal: varios trabajadores del proyecto el mismo día, en una sola transacción (todo o nada). */
export const groupEntryInput = z.object({
    date: isoDate,
    entries: z
        .array(z.object({
        assignmentId: z.uuid(),
        kind: z.enum(['work', 'per_diem', 'transport']).default('work'),
        timeIn: hhmm.nullable().optional(),
        timeOut: hhmm.nullable().optional(),
        lunchMinutes: z.coerce.number().int().min(0).max(240).nullable().optional(),
        quantity: z.coerce.number().int().min(1).max(31).nullable().optional(),
        notes: optionalText(500),
    }))
        .min(1, 'Elegí al menos un trabajador')
        .max(200, 'Máximo 200 registros por vez'),
});
/* ---------------------------- hoja semanal ---------------------------- */
export const payrollQuery = z
    .object({
    from: isoDate,
    to: isoDate,
})
    .refine((v) => v.from <= v.to, { path: ['to'], message: 'El final debe ser posterior al inicio' })
    .refine((v) => (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000 <= 62, {
    path: ['to'],
    message: 'Máximo 62 días',
});
/** Genera (o actualiza) el salario de las quincenas que tocan el rango. */
export const salaryGenerateInput = z
    .object({
    from: isoDate,
    to: isoDate,
})
    .refine((v) => v.from <= v.to, { path: ['to'], message: 'El final debe ser posterior al inicio' })
    .refine((v) => (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000 <= 366, {
    path: ['to'],
    message: 'Máximo un año',
});
/* ---------------------------- feriados ---------------------------- */
export const HOLIDAY_TYPES = [
    { code: 'obligatorio', name: 'Pago obligatorio' },
    { code: 'no_obligatorio', name: 'Pago no obligatorio' },
];
export const holidayType = z.enum(['obligatorio', 'no_obligatorio']);
export const holidayInput = z.object({
    date: isoDate,
    name: z.string().trim().min(3, 'Escribí el nombre').max(120),
    type: holidayType.default('obligatorio'),
    notes: optionalText(500),
    source: optionalText(300),
});
export const holidayListQuery = z.object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
});
//# sourceMappingURL=payroll.js.map